import React from "react";

import { filter, find, intersection, isEmpty, map } from "lodash/fp";

import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import useUserSettings from "@app/analytics/hooks/settings/useUserSettings";
import useFeatureAccess from "@app/common/hooks/useFeatureAccess";
import ROUTES from "@app/common/routes";
import type { UserDistributionSetting } from "@app/common/types";
import * as UserSettingsUtils from "@app/common/utils/userSettingsUtils";

import InfoPanel from "@rui/InfoPanel";
import { Link } from "@rui/Link";

import { PRE_READ_DISTRIBUTION_CHANNELS } from "../constants";
import DistributionChannelRow from "../DistributionChannelRow";
import ReportingSettingAccordion from "../ReportingSettingAccordion";
import { AccordionDetailsContent } from "../styles";
import * as ReportingSettingUtils from "../utils";

const DEFAULT_PRE_READ_DISTRIBUTION: Omit<UserDistributionSetting, "method"> = {
  category: "meeting_pre_read",
  isEnabled: true,
  frequency: "instant",
  sendTo: "participants_with_access",
};

const PreReadDistributionSettings = () => {
  const { userSettings, updateUserSettings } = useUserSettings();
  const featureAccess = useFeatureAccess();
  const { isProviderConnected } = useIntegrations();

  const preReadSettings = ReportingSettingUtils.getSettingsForCategory(
    userSettings,
    "meeting_pre_read"
  );
  const enabledChannelSettings =
    ReportingSettingUtils.getEnabledChannelSettings(preReadSettings);
  const settingDisabledOpts = UserSettingsUtils.getSettingDisabledOpts({
    settingIsEditable: featureAccess.canEditSendMeetingPreRead,
  });

  // channels that the user is able to connect (excludes channels that have been disabled by the Workspace)
  const connectableChannels: UserDistributionSetting["method"][] = map(
    "channel",
    filter(
      (c) => !c.providerId || featureAccess.canAccessIntegration(c.providerId),
      PRE_READ_DISTRIBUTION_CHANNELS
    )
  );

  // channels that are currently connected
  const connectedChannels: UserDistributionSetting["method"][] = map(
    "channel",
    filter(
      (c) => !c.providerId || isProviderConnected({ providerId: c.providerId }),
      PRE_READ_DISTRIBUTION_CHANNELS
    )
  );

  const availableChannelsForUser = intersection(
    connectableChannels,
    connectedChannels
  );

  const handleToggleEnabled = (enable: boolean) => {
    const updatedSettings =
      enable && isEmpty(preReadSettings)
        ? availableChannelsForUser.map((method) => ({
            ...DEFAULT_PRE_READ_DISTRIBUTION,
            method,
          }))
        : preReadSettings.map((setting) => ({
            ...setting,
            isEnabled: enable,
          }));

    updateUserSettings.mutate({ userDistributionSettings: updatedSettings });
  };

  const handleChangeChannelConfig = (
    updatedSetting: UserDistributionSetting
  ) => {
    updateUserSettings.mutate({ userDistributionSettings: [updatedSetting] });
  };

  const handleToggleChannelEnabled = (
    channel: UserDistributionSetting["method"],
    enable: boolean,
    existingSetting?: UserDistributionSetting
  ) => {
    const updatedSetting = existingSetting
      ? { ...existingSetting, isEnabled: enable }
      : {
          ...DEFAULT_PRE_READ_DISTRIBUTION,
          method: channel,
          isEnabled: enable,
        };
    handleChangeChannelConfig(updatedSetting);
  };

  return (
    <ReportingSettingAccordion
      title="Meeting Pre-Read"
      subtitle="Send a recap of the previous meeting before an upcoming meeting"
      enabledChannels={enabledChannelSettings}
      onToggleEnabled={handleToggleEnabled}
      opts={{
        disabled: settingDisabledOpts.disabled,
        disabledTooltip: settingDisabledOpts.tooltip ?? "",
      }}
    >
      <InfoPanel severity="info" sx={{ borderRadius: 0 }}>
        Pre-Reads can only be sent for recurring calendar events. When
        "Participants with access" is selected, it is based on your{" "}
        <Link to={ROUTES.USER_SETTINGS_REPORTING}>
          internal and external access settings
        </Link>
        .
      </InfoPanel>
      <AccordionDetailsContent>
        {PRE_READ_DISTRIBUTION_CHANNELS.map((channel) => {
          const userSetting = find(
            { method: channel.channel },
            preReadSettings
          );

          const disabledReason = ReportingSettingUtils.getChannelDisabledReason(
            {
              isConnected: connectedChannels.includes(channel.channel),
              isDisabled: !connectableChannels.includes(channel.channel),
            }
          );

          return (
            <DistributionChannelRow
              key={channel.channel}
              config={channel}
              name="meeting-pre-read-distribution"
              defaultFrequency={DEFAULT_PRE_READ_DISTRIBUTION.frequency}
              defaultSendTo={DEFAULT_PRE_READ_DISTRIBUTION.sendTo}
              disabledReason={disabledReason}
              onSettingChange={handleChangeChannelConfig}
              onToggleEnabled={handleToggleChannelEnabled}
              setting={userSetting}
            />
          );
        })}
      </AccordionDetailsContent>
    </ReportingSettingAccordion>
  );
};

export default PreReadDistributionSettings;
